import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { desc, eq } from "drizzle-orm";
import { Calendar, MapPin, Trophy } from "lucide-react";
import { getDb } from "@/db";
import { events } from "@/db/schema";

const getEvents = createServerFn({ method: "GET" }).handler(async () => {
	return getDb()
		.select()
		.from(events)
		.where(eq(events.visibility, "public"))
		.orderBy(desc(events.date));
});

export const Route = createFileRoute("/events/")({
	loader: () => getEvents(),
	component: EventsPage,
});

function statusBadgeClass(status: string) {
	switch (status) {
		case "setup":
			return "bg-status-setup text-foreground";
		case "qualifying":
			return "bg-status-qualifying text-foreground";
		case "finals":
			return "bg-status-finals text-primary-foreground";
		default:
			return "bg-status-complete text-foreground";
	}
}

function EventsPage() {
	const eventList = Route.useLoaderData();

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-4xl mx-auto">
				<div className="flex items-center justify-between mb-8">
					<h1 className="text-4xl text-foreground flex items-center gap-3">
						<Trophy size={32} className="text-gold" />
						Events
					</h1>
				</div>

				{eventList.length === 0 ? (
					<div className="bg-card border-2 border-border rounded-xl p-12 text-center">
						<Trophy size={48} className="text-muted-foreground mx-auto mb-4" />
						<p className="text-foreground text-lg mb-2">No events yet</p>
						<p className="text-muted-foreground text-sm">
							An admin needs to create the first event.
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{eventList.map((event) => (
							<Link
								key={event.id}
								to="/events/$eventId"
								params={{ eventId: String(event.id) }}
								className="block bg-card border-2 border-border hover:border-cherry/40 rounded-xl p-6 transition-all"
							>
								<div className="flex items-start justify-between">
									<div className="flex items-start gap-4">
										{event.imageUrl && (
											<img
												src={event.imageUrl}
												alt={event.name}
												className="h-16 w-24 object-cover rounded shrink-0"
											/>
										)}
										<div>
											<h2 className="text-xl font-bold text-foreground mb-2">
												{event.name}
											</h2>
											<div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
												<span className="flex items-center gap-1">
													<Calendar size={14} />
													{event.date}
												</span>
												{event.location && (
													<span className="flex items-center gap-1">
														<MapPin size={14} />
														{event.location}
													</span>
												)}
												<span>{event.laneCount} lanes</span>
											</div>
										</div>
									</div>
									<span
										className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${statusBadgeClass(event.status)}`}
									>
										{event.status}
									</span>
								</div>
							</Link>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
